'use client';

/**
 * UserAvatar Component
 *
 * Standardized avatar component for displaying user initials or images.
 * Matches the navbar avatar styling for consistency across the app.
 *
 * @example
 * // With name - shows initials (e.g., "MT" for Marcus Thompson)
 * <UserAvatar name="Marcus Thompson" />
 *
 * // With email fallback
 * <UserAvatar email="marcus@example.com" />
 *
 * // With image
 * <UserAvatar name="Marcus Thompson" src="/avatar.jpg" />
 *
 * // Different sizes
 * <UserAvatar name="Marcus" size="small" />
 * <UserAvatar name="Marcus" size="large" />
 *
 * // Consumer variant (secondary color)
 * <UserAvatar name="Customer" colorVariant="consumer" />
 */

import { Avatar, type AvatarProps } from '@mui/material';
import type React from 'react';

export interface UserAvatarProps extends Omit<AvatarProps, 'children'> {
  /** User's full name - used to generate initials */
  name?: string;
  /** User's email - fallback for initials if no name */
  email?: string;
  /** Avatar image URL */
  src?: string;
  /** Size variant. `xlarge` is for profile-detail hero avatars where the
   * avatar is the focal element of a card or page. */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** Color variant - user (primary) or consumer (secondary) */
  colorVariant?: 'user' | 'consumer';
}

/** Get initials from name (up to 2 characters) */
function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      // First + Last initial (e.g., "MT" for "Marcus Thompson")
      return `${parts[0]?.[0] || ''}${parts[parts.length - 1]?.[0] || ''}`.toUpperCase();
    }
    // Single name - just first letter
    return (parts[0]?.[0] || '').toUpperCase();
  }

  if (email) {
    // Use first letter of email
    return (email[0] || '').toUpperCase();
  }

  return '?';
}

/** Size configurations.
 *
 * Tuned tighter than MUI's default Avatar (40px) — most table-row and
 * list-row uses want a compact identity tag, not a 40px circle that
 * dominates the cell. Larger sizes are still available for profile-card /
 * settings-page contexts where the avatar is the focal element.
 *
 * Initials weight bumps to 700 so the letters stay legible after the
 * font-size shrink (lighter weights blur into the colored background at
 * 10–12px). */
const SIZES = {
  small: { width: 20, height: 20, fontSize: 10, fontWeight: 700 },
  medium: { width: 28, height: 28, fontSize: 12, fontWeight: 700 },
  large: { width: 36, height: 36, fontSize: 14, fontWeight: 700 },
  // Profile-detail hero — the avatar is the focal element of a card / page,
  // not a row identifier. Drops the weight back to 600 because at 18px the
  // letters are large enough that 700 looks chunky.
  xlarge: { width: 56, height: 56, fontSize: 18, fontWeight: 600 },
} as const;

export const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  email,
  src,
  size = 'medium',
  colorVariant = 'user',
  sx,
  ...props
}) => {
  const sizeConfig = SIZES[size];
  const initials = getInitials(name, email);

  return (
    <Avatar
      src={src}
      alt={name || email || 'User'}
      sx={{
        width: sizeConfig.width,
        height: sizeConfig.height,
        bgcolor: colorVariant === 'user' ? 'primary.main' : 'secondary.main',
        color: colorVariant === 'user' ? 'primary.contrastText' : 'secondary.contrastText',
        fontSize: sizeConfig.fontSize,
        fontWeight: sizeConfig.fontWeight,
        ...sx,
      }}
      {...props}
    >
      {initials}
    </Avatar>
  );
};

export default UserAvatar;
